import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';

// Shared building blocks extracted from the contract/payment "mini card" family
// (ContractDateRangeCard, ContractMetricBulletsCard, NextPaymentDueCard, ContractDetailsCard,
// SingleContractMetricCard, ContractsCard, ScheduledPaymentsCard). These components render
// byte-for-byte the same markup/styles the call sites previously inlined — extracted here purely
// to remove duplication, with no visual/behavioral change.

/** Outer bordered/rounded white card shell used by the per-contract mini cards. */
export const CardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            border: `1px solid ${colors.greyPreset[30]}`,
            borderRadius: '12px',
            backgroundColor: colors.fullWhite,
            overflow: 'hidden',
        }}
    >
        {children}
    </Box>
);

/** Blue header row showing "#<contractId>" on the left, with optional content on the right. */
export const CardHeaderRow: React.FC<{ contractId: React.ReactNode; right?: React.ReactNode }> = ({
    contractId,
    right,
}) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.55, backgroundColor: colors.bluePreset[10] }}
    >
        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.fullWhite }}>
            #{contractId}
        </Typography>
        {right}
    </Stack>
);

/** Rounded pill badge used for status/frequency/type chips in card header rows. */
export const BadgePill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        variant="caption"
        sx={{
            px: 1,
            py: 0.25,
            borderRadius: '999px',
            backgroundColor: colors.fullWhite,
            color: colors.fullBlack,
            fontWeight: 700,
            fontSize: '0.72rem',
        }}
    >
        {children}
    </Typography>
);

/** Grey label/value metric tile used inside the per-contract mini cards' body grids. */
export const MetricTile: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ backgroundColor: colors.greyPreset[90], borderRadius: '8px', p: 0.65 }}>
        <Typography variant="body2" sx={{ color: colors.greyPreset[60], fontSize: '0.72rem', fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
            {value}
        </Typography>
    </Box>
);

/** Bold intro line shown above the list of per-contract mini cards (trailing colon stripped). */
export const CardIntro: React.FC<{ text: string }> = ({ text }) => (
    <Typography
        variant="body2"
        sx={{ mb: 1, color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.45 }}
    >
        {text.replace(/:\s*$/, '')}
    </Typography>
);

/** Trailing follow-up line shown below the list of per-contract mini cards. Renders nothing when empty. */
export const CardFollowUp: React.FC<{ text?: string | null }> = ({ text }) =>
    text ? (
        <Typography variant="body2" sx={{ mt: 0.8, color: colors.fullBlack }}>
            {text}
        </Typography>
    ) : null;

/** Hover-elevation card style shared by ContractsCard's and ScheduledPaymentsCard's list items. */
export const HOVER_CARD_SX: SxProps<Theme> = {
    borderRadius: '12px',
    backgroundColor: colors.fullWhite,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.greyPreset[20]}`,
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    },
};

// --- Asset-card building blocks shared by ContractMetricListCard, GenericBotResponseCard, and
// LinkedAssetsCard (all three render "asset" tiles/cards from parsed contract metric payloads). ---

/** The rounded 54x54 truck-icon avatar shown at the left of every asset card/row. */
export const AssetAvatarIcon: React.FC = () => (
    <Box
        sx={{
            width: 54,
            height: 54,
            borderRadius: '12px',
            backgroundColor: '#4C6298',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            flexShrink: 0,
        }}
    >
        <span role="img" aria-label="asset" style={{ color: colors.fullWhite, lineHeight: 1 }}>
            🚚
        </span>
    </Box>
);

/**
 * Single label/value tile used inside asset detail grids (Serial Number/Make/Model/Year/etc).
 * The value wraps as "break-all" specifically for the Serial Number field (long VINs), and
 * "break-word" for everything else — matching the original per-callsite conditional exactly.
 */
export const AssetFieldTile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box
        sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.15,
            px: 0.75,
            py: 0.65,
            borderRadius: '10px',
            border: `1px solid ${colors.greyPreset[20]}`,
            backgroundColor: colors.greyPreset[5],
        }}
    >
        <Typography variant="caption" sx={{ color: colors.greyPreset[60], fontWeight: 700, display: 'block', mb: 0 }}>
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                color: colors.fullBlack,
                fontWeight: 700,
                lineHeight: 1.35,
                wordBreak: label === 'Serial Number' ? 'break-all' : 'break-word',
                overflowWrap: 'anywhere',
            }}
        >
            {value}
        </Typography>
    </Box>
);

/** Responsive 2-col grid of AssetFieldTile entries, used for every asset detail row grouping. */
export const AssetFieldGrid: React.FC<{ fields: Array<[string, React.ReactNode]>; keyPrefix: string }> = ({
    fields,
    keyPrefix,
}) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 0.75 }}>
        {fields.map(([label, value]) => (
            <AssetFieldTile key={`${keyPrefix}-${label}`} label={label} value={value} />
        ))}
    </Box>
);

/** Full-width "Location" tile shown below the asset detail grids. */
export const AssetLocationTile: React.FC<{ location: React.ReactNode }> = ({ location }) => (
    <Box
        sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.15,
            px: 0.8,
            py: 0.7,
            borderRadius: '10px',
            border: `1px solid ${colors.greyPreset[20]}`,
            backgroundColor: '#F8FAFC',
        }}
    >
        <Typography variant="caption" sx={{ color: colors.greyPreset[60], fontWeight: 700, display: 'block', mb: 0 }}>
            Location
        </Typography>
        <Typography
            variant="body2"
            sx={{
                color: colors.fullBlack,
                fontWeight: 600,
                lineHeight: 1.35,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
            }}
        >
            {location}
        </Typography>
    </Box>
);

/**
 * Rounded/bordered white shell (with soft shadow) used to wrap a single asset "tile" — the
 * per-item card rendered by LinkedAssetsCard, ContractMetricListCard, and GenericBotResponseCard.
 */
export const AssetCardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            border: `1px solid ${colors.greyPreset[20]}`,
            borderRadius: '14px',
            backgroundColor: colors.fullWhite,
            boxShadow: '0 1px 6px rgba(16, 24, 40, 0.06)',
            px: 1.1,
            py: 1,
        }}
    >
        {children}
    </Box>
);

// --- Plain-text response card shells shared across PlainTextResponseCard's own render branches
// (profile summary / narrative sections share one shell, guidance / plain-answer share another). ---

/** Rounded white card shell (radius 14, padding 1.2) used by PlainTextResponseCard's profile-summary and narrative-section branches. */
export const SoftRoundedCardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            border: `1px solid ${colors.greyPreset[20]}`,
            borderRadius: '14px',
            p: 1.2,
            backgroundColor: colors.fullWhite,
        }}
    >
        {children}
    </Box>
);

/** Rounded white card shell (radius 16, padding 1, no shadow) used by PlainTextResponseCard's guidance and plain-answer branches. */
export const GuidanceCardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            border: `1px solid ${colors.greyPreset[20]}`,
            borderRadius: '16px',
            p: 1,
            backgroundColor: colors.fullWhite,
            boxShadow: 'none',
        }}
    >
        {children}
    </Box>
);

/**
 * Compact card rendered when an asset section/row carries only a serial number tied to a
 * contract (no name/description/make/model/location) — used by both ContractMetricListCard and
 * GenericBotResponseCard for that exact scenario.
 */
export const ContractSerialOnlyAssetCard: React.FC<{ contractId: React.ReactNode; serialNumber: React.ReactNode }> = ({
    contractId,
    serialNumber,
}) => (
    <AssetCardShell>
        <Stack direction="row" gap={0.85} alignItems="center" sx={{ minWidth: 0 }}>
            <AssetAvatarIcon />

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    variant="body2"
                    sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }}
                >
                    {`Contract ${contractId}`}
                </Typography>
            </Box>

            <Box
                sx={{
                    minWidth: 0,
                    maxWidth: '58%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.05,
                    px: 0.75,
                    py: 0.55,
                    borderRadius: '10px',
                    border: `1px solid ${colors.greyPreset[20]}`,
                    backgroundColor: colors.greyPreset[5],
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ color: colors.greyPreset[60], fontWeight: 700, display: 'block', mb: 0 }}
                >
                    Serial Number
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: colors.fullBlack,
                        fontWeight: 700,
                        lineHeight: 1.25,
                        wordBreak: 'break-all',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {serialNumber}
                </Typography>
            </Box>
        </Stack>
    </AssetCardShell>
);
